import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Award, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CertificateSheet } from '../components/course-builder/CertificateSheet';
import { CertificateLocked } from '../components/course-builder/CertificateLocked';

const PRINT_CSS = `
@page { size: A4 landscape; margin: 10mm; }
@media print {
  html[data-shot-shield] body::after { display: none !important; }
  body * { visibility: hidden !important; }
  #course-certificate-sheet, #course-certificate-sheet * { visibility: visible !important; }
  #course-certificate-sheet { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
@keyframes cert-reveal { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
.cert-reveal { animation: cert-reveal 500ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
`;

const Unavailable = ({ message, homePath }) => (
  <Card data-testid="certificate-unavailable">
    <CardContent className="flex flex-col items-center py-16 text-center">
      <Award className="h-14 w-14 text-muted-foreground/50" />
      <h2 className="mt-4 font-heading text-xl font-semibold">Certificate not available</h2>
      <p className="mt-1 text-muted-foreground">{message}</p>
      <Button asChild className="mt-6"><Link to={homePath}>Back to my courses</Link></Button>
    </CardContent>
  </Card>
);

// Certificate for a course builder course: Tech Learning, Finance Training or a Course Builder module
export default function CourseCertificatePage({ apiBase, homePath, courseBase }) {
  const { courseId } = useParams();
  const { api } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const coursePath = `${courseBase}/${courseId}`;

  useEffect(() => {
    let live = true;
    setState({ loading: true, data: null, error: null });
    api.get(`${apiBase}/builder-courses/${courseId}/certificate`)
      .then((r) => live && setState({ loading: false, data: r.data, error: null }))
      .catch((e) => live && setState({ loading: false, data: null, error: e.response?.data?.detail || 'Could not load this certificate' }));
    return () => { live = false; };
  }, [api, apiBase, courseId]);

  const { loading, data, error } = state;
  return (
    <MainLayout>
      <style>{PRINT_CSS}</style>
      <div className="mx-auto max-w-5xl space-y-6" data-testid="course-certificate-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="ghost" asChild>
            <Link to={coursePath} data-testid="certificate-back-to-course"><ArrowLeft className="mr-2 h-4 w-4" />Back to course</Link>
          </Button>
          {data?.earned && (
            <Button onClick={() => window.print()} data-testid="certificate-download-btn">
              <Download className="mr-2 h-4 w-4" />Download / Print
            </Button>
          )}
        </div>
        {loading ? (
          <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : error ? (
          <Unavailable message={error} homePath={homePath} />
        ) : data.earned ? (
          <CertificateSheet certificate={data.certificate} />
        ) : (
          <CertificateLocked data={data} coursePath={coursePath} />
        )}
      </div>
    </MainLayout>
  );
}
